import type {
  DroneAlertThresholds,
  DroneCredentialMode,
  DroneEndpoint,
  DroneProtocol,
  Role,
} from './types';
import { ROLES } from './roles';
import { STALE_AFTER_MAX_MS, STALE_AFTER_MIN_MS, TELEMETRY_RATE_MAX_HZ, TELEMETRY_RATE_MIN_HZ } from './defaults';
import { clamp, safeNumber, safeString } from './math';

export interface FieldError {
  field: string;
  message: string;
}

export type ValidationResult = { ok: true } | { ok: false; errors: FieldError[] };

/* ------------------------------------------------------------------ *
 * 基础校验
 * ------------------------------------------------------------------ */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const HOSTNAME_RE = /^(?=.{1,253}$)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const IPV4_RE = /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

export function isIpv4(value: string): boolean {
  return IPV4_RE.test(value.trim());
}

/**
 * 接受域名或 IPv4。允许 `localhost`，便于本地对接 mock 无人机。
 * 刻意不接受 URL、路径、协议前缀，避免主机字段被塞入非法内容。
 */
export function isValidHost(value: string): boolean {
  const host = value.trim();
  if (host === '') return false;
  if (host === 'localhost') return true;
  if (isIpv4(host)) return true;
  if (host.includes(':') || host.includes('/') || host.includes(' ')) return false;
  return HOSTNAME_RE.test(host);
}

export function isValidPort(value: unknown): boolean {
  const port = safeNumber(value, Number.NaN);
  return Number.isInteger(port) && port >= 1 && port <= 65535;
}

/** 归一化 REST 根路径：始终以 `/` 开头，不以 `/` 结尾，空值退化为 `/`。 */
export function normalizeBasePath(value: string): string {
  let path = safeString(value, '').trim();
  if (path === '' || path === '/') return '';
  if (!path.startsWith('/')) path = `/${path}`;
  return path.replace(/\/+$/, '');
}

export function isValidBasePath(value: string): boolean {
  const path = value.trim();
  if (path === '') return true;
  if (!path.startsWith('/')) return false;
  if (path.includes('..') || path.includes('?') || path.includes('#')) return false;
  return /^\/[A-Za-z0-9\-._~!$&'()*+,;=:@/]*$/.test(path);
}

export function isValidWsPath(value: string): boolean {
  const path = value.trim();
  if (path === '') return false;
  return isValidBasePath(path);
}

const PW_MIN = 10;
const PW_MAX = 200;

/** 返回 null 表示通过，否则返回中文错误说明。 */
export function validatePassword(password: string): string | null {
  if (password.length < PW_MIN) return `密码长度不得少于 ${PW_MIN} 位`;
  if (password.length > PW_MAX) return `密码长度不得超过 ${PW_MAX} 位`;
  if (!/[A-Za-z]/.test(password)) return '密码必须包含至少一个字母';
  if (!/\d/.test(password)) return '密码必须包含至少一个数字';
  return null;
}

/** 生成强随机密码，供管理员创建用户时使用。 */
export function generatePassword(length = 16): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const byte of bytes) {
    out += alphabet[byte % alphabet.length] ?? 'a';
  }
  // 保证一定同时含字母与数字，避免生成的密码自己不满足策略
  return `${out}7`;
}

export function isRoleValue(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

const CREDENTIAL_MODES: readonly DroneCredentialMode[] = ['platformToken', 'apiKey', 'none'];
const PROTOCOLS: readonly DroneProtocol[] = ['http', 'https'];

export function isCredentialMode(value: unknown): value is DroneCredentialMode {
  return typeof value === 'string' && (CREDENTIAL_MODES as readonly string[]).includes(value);
}

export function isProtocol(value: unknown): value is DroneProtocol {
  return typeof value === 'string' && (PROTOCOLS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ *
 * 无人机注册表校验
 * ------------------------------------------------------------------ */

export interface DroneEndpointInput {
  protocol?: unknown;
  host?: unknown;
  port?: unknown;
  basePath?: unknown;
  wsPath?: unknown;
}

export function validateDroneEndpoint(input: DroneEndpointInput): FieldError[] {
  const errors: FieldError[] = [];

  if (!isProtocol(input.protocol)) {
    errors.push({ field: 'endpoint.protocol', message: '协议仅支持 http 或 https' });
  }

  const host = safeString(input.host, '');
  if (host.trim() === '') {
    errors.push({ field: 'endpoint.host', message: '域名或 IP 不能为空' });
  } else if (!isValidHost(host)) {
    errors.push({ field: 'endpoint.host', message: '域名或 IP 格式不正确（不要包含协议或路径）' });
  }

  if (!isValidPort(input.port)) {
    errors.push({ field: 'endpoint.port', message: '端口需为 1–65535 之间的整数' });
  }

  const basePath = safeString(input.basePath, '');
  if (!isValidBasePath(basePath)) {
    errors.push({ field: 'endpoint.basePath', message: 'API 根路径需以 / 开头，且不得包含 .. ? #' });
  }

  const wsPath = safeString(input.wsPath, '');
  if (!isValidWsPath(wsPath)) {
    errors.push({ field: 'endpoint.wsPath', message: 'WebSocket 路径需以 / 开头' });
  }

  return errors;
}

export interface DroneInput {
  name?: unknown;
  uniqueId?: unknown;
  model?: unknown;
  vendor?: unknown;
  endpoint?: DroneEndpointInput;
  credentialMode?: unknown;
  credential?: unknown;
  telemetryRateHz?: unknown;
  staleAfterMs?: unknown;
  tags?: unknown;
  groupId?: unknown;
  notes?: unknown;
  thresholds?: unknown;
}

export function validateDroneInput(input: DroneInput): FieldError[] {
  const errors: FieldError[] = [];

  const name = safeString(input.name, '').trim();
  if (name === '') errors.push({ field: 'name', message: '名称不能为空' });
  else if (name.length > 60) errors.push({ field: 'name', message: '名称不得超过 60 个字符' });

  const uniqueId = safeString(input.uniqueId, '').trim();
  if (uniqueId === '') errors.push({ field: 'uniqueId', message: '机身唯一 ID 不能为空' });
  else if (uniqueId.length > 64) errors.push({ field: 'uniqueId', message: '机身唯一 ID 不得超过 64 个字符' });
  else if (!/^[A-Za-z0-9\-_.:]+$/.test(uniqueId)) {
    errors.push({ field: 'uniqueId', message: '机身唯一 ID 仅允许字母、数字与 - _ . :' });
  }

  if (safeString(input.model, '').trim() === '') {
    errors.push({ field: 'model', message: '型号不能为空' });
  }

  errors.push(...validateDroneEndpoint(input.endpoint ?? {}));

  if (!isCredentialMode(input.credentialMode)) {
    errors.push({ field: 'credentialMode', message: '凭据模式不合法' });
  }

  const mode = input.credentialMode;
  const credential = safeString(input.credential, '');
  if ((mode === 'apiKey' || mode === 'platformToken') && credential !== '' && credential.length < 8) {
    errors.push({ field: 'credential', message: '凭据长度不得少于 8 位' });
  }

  const rate = safeNumber(input.telemetryRateHz, Number.NaN);
  if (Number.isFinite(rate) && (rate < TELEMETRY_RATE_MIN_HZ || rate > TELEMETRY_RATE_MAX_HZ)) {
    errors.push({
      field: 'telemetryRateHz',
      message: `遥测频率需在 ${TELEMETRY_RATE_MIN_HZ}–${TELEMETRY_RATE_MAX_HZ} Hz 之间`,
    });
  }

  const stale = safeNumber(input.staleAfterMs, Number.NaN);
  if (Number.isFinite(stale) && (stale < STALE_AFTER_MIN_MS || stale > STALE_AFTER_MAX_MS)) {
    errors.push({
      field: 'staleAfterMs',
      message: `失联判定阈值需在 ${STALE_AFTER_MIN_MS}–${STALE_AFTER_MAX_MS} ms 之间`,
    });
  }

  if (input.tags !== undefined && !Array.isArray(input.tags)) {
    errors.push({ field: 'tags', message: '标签必须是数组' });
  } else if (Array.isArray(input.tags) && input.tags.length > 12) {
    errors.push({ field: 'tags', message: '标签数量不得超过 12 个' });
  }

  if (safeString(input.notes, '').length > 500) {
    errors.push({ field: 'notes', message: '备注不得超过 500 个字符' });
  }

  return errors;
}

/* ------------------------------------------------------------------ *
 * 用户校验
 * ------------------------------------------------------------------ */

export interface UserInput {
  email?: unknown;
  name?: unknown;
  role?: unknown;
  status?: unknown;
  password?: unknown;
}

export const USER_NAME_MAX = 40;

export function validateUserInput(input: UserInput, options: { requirePassword: boolean }): FieldError[] {
  const errors: FieldError[] = [];

  const email = safeString(input.email, '').trim().toLowerCase();
  if (email === '') errors.push({ field: 'email', message: '邮箱不能为空' });
  else if (!isValidEmail(email)) errors.push({ field: 'email', message: '邮箱格式不正确' });
  else if (email.length > 254) errors.push({ field: 'email', message: '邮箱过长' });

  const name = safeString(input.name, '').trim();
  if (name === '') errors.push({ field: 'name', message: '姓名不能为空' });
  else if (name.length > USER_NAME_MAX) {
    errors.push({ field: 'name', message: `姓名不得超过 ${USER_NAME_MAX} 个字符` });
  }

  if (!isRoleValue(input.role)) errors.push({ field: 'role', message: '角色不合法' });

  if (input.status !== undefined && input.status !== 'active' && input.status !== 'suspended') {
    errors.push({ field: 'status', message: '状态不合法' });
  }

  const password = safeString(input.password, '');
  if (options.requirePassword || password !== '') {
    const problem = validatePassword(password);
    if (problem) errors.push({ field: 'password', message: problem });
  }

  return errors;
}

/* ------------------------------------------------------------------ *
 * 阈值校验
 * ------------------------------------------------------------------ */

/** 阈值必须满足 caution ≥ warning ≥ critical（对「下限型」指标而言）。 */
export function validateThresholds(input: unknown): FieldError[] {
  if (input === undefined || input === null) return [];
  if (typeof input !== 'object') return [{ field: 'thresholds', message: '阈值必须是对象' }];

  const errors: FieldError[] = [];
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (value === undefined || value === null) continue;
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      errors.push({ field: `thresholds.${key}`, message: '阈值必须是有限数字' });
    }
  }
  return errors;
}

export function sanitizeThresholds(input: unknown): Partial<DroneAlertThresholds> {
  if (input === null || typeof input !== 'object') return {};
  const out: Partial<DroneAlertThresholds> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    const numeric = safeNumber(value, Number.NaN);
    if (Number.isFinite(numeric)) {
      (out as Record<string, number>)[key] = clamp(numeric, -1_000_000, 1_000_000);
    }
  }
  return out;
}

/** 拼接注册表端点的可读形式，用于列表展示。 */
export function formatEndpoint(endpoint: DroneEndpoint): string {
  return `${endpoint.protocol}://${endpoint.host}:${endpoint.port}${normalizeBasePath(endpoint.basePath)}`;
}
